import { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { FaFlask, FaPlus, FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
import trialDeploymentsApi from '../../../services/trialDeploymentsApi';
import monitoringApi from '../../../services/monitoringApi';
import { ChartCard, EmptyState, doughnutOptions } from '../analytics/analyticsShared';

const STATUS_COLORS = {
  planned: 'bg-blue-50 text-blue-700 ring-blue-200',
  active: 'bg-green-50 text-green-700 ring-green-200',
  completed: 'bg-gray-50 text-gray-600 ring-gray-200',
  discontinued: 'bg-red-50 text-red-700 ring-red-200',
};

const EMPTY_FORM = {
  name: '',
  customer: '',
  devices: [],
  objective: '',
  startDate: new Date().toISOString().slice(0, 10),
  endDate: '',
  status: 'planned',
  outcomeSummary: '',
};

// A real record of pilot/trial deployments at customer farms — direct R&D
// evidence. Admin creates an entry when a trial actually starts and
// updates outcomeSummary as it progresses; nothing here is templated.
function TrialDeployments() {
  const [trials, setTrials] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // trial or 'new'
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const [trialsRes, devicesRes] = await Promise.all([
        trialDeploymentsApi.list(),
        monitoringApi.listDevices(),
      ]);
      setTrials(trialsRes.trials || []);
      setDevices(devicesRes.devices || []);
    } catch (err) {
      /* interceptor toasts */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const statusChartData = useMemo(() => {
    const colors = { planned: '#5B7FBF', active: '#1e5c33', completed: '#6b7280', discontinued: '#dc2626' };
    const keys = ['planned', 'active', 'completed', 'discontinued'];
    const counts = keys.map((k) => trials.filter((t) => t.status === k).length);
    const nonZero = keys.map((k, i) => ({ k, c: counts[i] })).filter((x) => x.c > 0);
    const use = nonZero.length ? nonZero : keys.map((k) => ({ k, c: 0 }));
    return {
      labels: use.map((x) => x.k.charAt(0).toUpperCase() + x.k.slice(1)),
      datasets: [{ data: use.map((x) => x.c), backgroundColor: use.map((x) => colors[x.k]), borderWidth: 0 }],
    };
  }, [trials]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing('new');
  };

  const openEdit = (trial) => {
    setForm({
      ...EMPTY_FORM,
      ...trial,
      customer: trial.customer?._id || '',
      devices: (trial.devices || []).map((d) => d._id),
      startDate: trial.startDate ? trial.startDate.slice(0, 10) : '',
      endDate: trial.endDate ? trial.endDate.slice(0, 10) : '',
    });
    setEditing(trial._id);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  // Selecting a device auto-fills the customer, since a device already belongs to one farmer.
  const handleDeviceToggle = (deviceId) => {
    setForm((f) => {
      const has = f.devices.includes(deviceId);
      const nextDevices = has ? f.devices.filter((id) => id !== deviceId) : [...f.devices, deviceId];
      const device = devices.find((d) => d._id === deviceId);
      const customer = !has && device?.farmer?._id ? device.farmer._id : f.customer;
      return { ...f, devices: nextDevices, customer };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, endDate: form.endDate || null };
      if (editing === 'new') {
        await trialDeploymentsApi.create(payload);
      } else {
        await trialDeploymentsApi.update(editing, payload);
      }
      closeForm();
      load();
    } catch (err) {
      /* interceptor toasts */
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this trial deployment record?')) return;
    try {
      await trialDeploymentsApi.remove(id);
      load();
    } catch (err) {
      /* interceptor toasts */
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading trial deployments…</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Trial &amp; Pilot Deployments</h1>
          <p className="text-xs text-gray-400 mt-1">
            Real pilot programs at customer farms — objective, dates, devices involved, and outcome.
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90"
        >
          <FaPlus /> Log a trial
        </button>
      </div>

      {trials.length > 0 && (
        <ChartCard title="Trials by status" className="h-64 max-w-md">
          <div className="h-48">
            <Doughnut data={statusChartData} options={doughnutOptions} />
          </div>
        </ChartCard>
      )}

      {editing && (
        <ChartCard title={editing === 'new' ? 'Log a new trial deployment' : 'Edit trial deployment'}>
          <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Trial name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                placeholder="e.g. House A climate monitoring pilot — Siraj Farm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Devices / houses involved</label>
              <div className="flex flex-wrap gap-2">
                {devices.map((d) => (
                  <label key={d._id} className={`text-xs px-2 py-1 rounded-full border cursor-pointer ${form.devices.includes(d._id) ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-600'}`}>
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={form.devices.includes(d._id)}
                      onChange={() => handleDeviceToggle(d._id)}
                    />
                    {d.houseName || d.name} ({d.farmer?.firstName})
                  </label>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Objective — what this trial is meant to test</label>
              <textarea
                required
                value={form.objective}
                onChange={(e) => setForm({ ...form, objective: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start date</label>
              <input
                type="date" required
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End date (if concluded)</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              >
                {Object.keys(STATUS_COLORS).map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Outcome summary (update as the trial progresses)</label>
              <textarea
                value={form.outcomeSummary}
                onChange={(e) => setForm({ ...form, outcomeSummary: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                rows={3}
                placeholder="What actually happened — real observations only."
              />
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <button type="button" onClick={closeForm} className="text-sm px-3 py-2 rounded-lg border border-gray-200 flex items-center gap-1">
                <FaTimes /> Cancel
              </button>
              <button type="submit" className="text-sm px-3 py-2 rounded-lg bg-primary text-white">Save</button>
            </div>
          </form>
        </ChartCard>
      )}

      {trials.length === 0 ? (
        <EmptyState
          icon={<FaFlask />}
          title="No trial deployments logged yet"
          message="When a real pilot starts at a customer farm, log it here — objective, devices, dates — and update the outcome as it runs."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {trials.map((t) => (
            <ChartCard key={t._id} title={t.name}>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${STATUS_COLORS[t.status]}`}>
                    {t.status}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(t.startDate).toLocaleDateString()} {t.endDate ? `– ${new Date(t.endDate).toLocaleDateString()}` : '– ongoing'}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  Customer: {t.customer?.firstName} {t.customer?.lastName} {t.customer?.companyName ? `(${t.customer.companyName})` : ''}
                </div>
                {t.devices?.length > 0 && (
                  <div className="text-xs text-gray-400">
                    Devices: {t.devices.map((d) => d.houseName || d.name).join(', ')}
                  </div>
                )}
                <p className="text-gray-600"><strong>Objective:</strong> {t.objective}</p>
                {t.outcomeSummary && <p className="text-gray-600"><strong>Outcome:</strong> {t.outcomeSummary}</p>}
                <div className="flex gap-3 pt-2">
                  <button onClick={() => openEdit(t)} className="text-xs text-primary flex items-center gap-1 hover:opacity-80">
                    <FaEdit /> Edit
                  </button>
                  <button onClick={() => handleDelete(t._id)} className="text-xs text-red-500 flex items-center gap-1 hover:opacity-80">
                    <FaTrash /> Remove
                  </button>
                </div>
              </div>
            </ChartCard>
          ))}
        </div>
      )}
    </div>
  );
}

export default TrialDeployments;
